const CHUNK_LOAD_ERROR_MESSAGES = [
  // Chromium
  'Failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // WebKit
  'Importing a module script failed',
  // WebKit, for a chunk response without CORS headers (e.g. a CDN error page)
  'Cross-origin script load denied',
];

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/**
 * WebKit and Firefox cancel in-flight chunk requests as soon as a navigation
 * to another document starts, while the page is still visible. Retrying then
 * is pointless, the page is about to go away. A navigation that has not
 * replaced the page after this long was abandoned (a download, a link to an
 * external app, the stop button).
 */
const ABANDONED_NAVIGATION_TIMEOUT_MS = 5000;

let leavingPageSince: number | undefined;

if (process.browser) {
  const markLeavingPage = () => {
    leavingPageSince = Date.now();
  };

  const navigation = 'navigation' in window ? window.navigation : undefined;

  if (navigation) {
    navigation.addEventListener('navigate', event => {
      if (!event.destination.sameDocument && !event.downloadRequest) {
        markLeavingPage();
      }
    });
  } else {
    // Fallback for browsers without the Navigation API only: a beforeunload
    // listener keeps pages out of Firefox's back/forward cache.
    window.addEventListener('beforeunload', markLeavingPage);
  }

  window.addEventListener('pageshow', () => {
    leavingPageSince = undefined;
  });
}

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  CHUNK_LOAD_ERROR_MESSAGES.some(message => error.message.includes(message));

type ImportModule = () => Promise<unknown>;
type VitePreload = (
  importModule: ImportModule,
  deps?: string[],
  importerUrl?: string,
) => Promise<unknown>;

/**
 * Chrome and Safari keep a failed module fetch for the lifetime of the page:
 * importing the same URL again rejects without a request. A retry therefore
 * loads a module from a URL the page has not seen yet, and every later import
 * of the module uses that URL so the module is only ever evaluated once.
 */
const recoveredModuleUrls = new Map<string, string>();
const cacheBustedAttempts = new Map<string, number>();
const pendingRetries = new Map<string, Promise<string>>();
let freshUrlImports: Promise<unknown> = Promise.resolve();

/**
 * Wraps Vite's preload helper, which `@aboutyou/bundler` puts around every
 * dynamic import of the client build.
 */
export const retryFailedChunkLoads =
  (vitePreload: VitePreload): VitePreload =>
  (importModule, deps, importerUrl) => {
    const moduleUrl = getModuleUrl(importModule, importerUrl);
    const takeFromModule = getTakeFromModule(importModule);
    const recoveredModuleUrl = moduleUrl && recoveredModuleUrls.get(moduleUrl);

    const loadModule =
      recoveredModuleUrl && takeFromModule
        ? import(/* @vite-ignore */ recoveredModuleUrl).then(takeFromModule)
        : vitePreload(importModule, deps, importerUrl);

    return loadModule.catch(error => {
      if (!moduleUrl || !takeFromModule || !isChunkLoadError(error)) {
        throw error;
      }

      return findLoadableModuleUrl(
        moduleUrl,
        getDependencyUrls(moduleUrl, deps),
        error,
      )
        .then(loadableModuleUrl => import(/* @vite-ignore */ loadableModuleUrl))
        .then(takeFromModule);
    });
  };

/**
 * Vite passes the import as `() => import("./chunk-[hash].js")` and the URL
 * of the importing chunk, so the chunk URL is read from the function source.
 */
const getModuleUrl = (importModule: ImportModule, importerUrl?: string) => {
  const specifier = /import\((["'`])(.+?)\1\)/.exec(
    importModule.toString(),
  )?.[2];

  return specifier && importerUrl
    ? new URL(specifier, importerUrl).href
    : undefined;
};

type ModuleNamespace = Record<string, unknown>;

/**
 * Vite moves what the code takes from a chunk into the import function, as in
 * `() => import("./chunk.js").then(m => m.x)` or
 * `async () => {const {x: a} = await import("./chunk.js"); return {y: a}}`.
 * A retry imports the chunk from another URL, so it takes the same from it.
 * Imports of any other shape are not retried, as their result would differ.
 */
const getTakeFromModule = (
  importModule: ImportModule,
): ((module: ModuleNamespace) => unknown) | undefined => {
  const source = importModule.toString();
  const importCall = String.raw`import\(\s*(?:"[^"]+"|'[^']+'|\`[^\`]+\`)\s*\)`;

  if (new RegExp(String.raw`^\(\)\s*=>\s*${importCall}$`).test(source)) {
    return module => module;
  }

  const takenExport = new RegExp(
    String.raw`^\(\)\s*=>\s*${importCall}\.then\(\(?([\w$]+)\)?\s*=>\s*\1\.([\w$]+)\)$`,
  ).exec(source)?.[2];

  if (takenExport) {
    return module => module[takenExport];
  }

  const destructured = new RegExp(
    String.raw`^async\s*\(\)\s*=>\s*\{\s*const\s*\{([^}]*)\}\s*=\s*await\s+${importCall};?\s*return\s*\{([^}]*)\};?\s*\}$`,
  ).exec(source);

  return destructured
    ? getTakeFromDestructuring(destructured[1]!, destructured[2]!)
    : undefined;
};

const getTakeFromDestructuring = (bindings: string, returned: string) => {
  const parsePairs = (list: string) =>
    list
      .split(',')
      .map(pair => pair.split(':').map(name => name.trim()))
      .map(([key = '', value = key]) => [key, value] as const);

  const exportByLocalName = new Map(
    parsePairs(bindings).map(([exportName, localName]) => [
      localName,
      exportName,
    ]),
  );
  const returnedExports = parsePairs(returned).map(
    ([key, localName]) => [key, exportByLocalName.get(localName)] as const,
  );

  if (returnedExports.some(([, exportName]) => !exportName)) {
    return undefined;
  }

  return (module: ModuleNamespace) =>
    Object.fromEntries(
      returnedExports.map(([key, exportName]) => [key, module[exportName!]]),
    );
};

/**
 * Vite lists every chunk the imported chunk depends on, through
 * `window.cdnPath`. The chunks import each other by relative path from one
 * folder, so a dependency is resolved next to the chunk to get the exact URL
 * the browser loaded it from.
 */
const getDependencyUrls = (moduleUrl: string, deps: string[] = []) =>
  deps
    .map(dep => new URL(dep, moduleUrl).pathname.split('/').pop() ?? '')
    .filter(fileName => fileName.endsWith('.js'))
    .map(fileName => new URL(fileName, moduleUrl).href)
    .filter(dependencyUrl => dependencyUrl !== moduleUrl);

const findLoadableModuleUrl = (
  moduleUrl: string,
  dependencyUrls: string[],
  error: unknown,
) => {
  let pendingRetry = pendingRetries.get(moduleUrl);

  if (!pendingRetry) {
    pendingRetry = retryModuleUrl(moduleUrl, dependencyUrls, error).finally(
      () => {
        pendingRetries.delete(moduleUrl);
      },
    );
    pendingRetries.set(moduleUrl, pendingRetry);
  }

  return pendingRetry;
};

const retryModuleUrl = async (
  moduleUrl: string,
  dependencyUrls: string[],
  error: unknown,
) => {
  let lastError = error;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    await waitBeforeRetry(attempt);

    // Browsers following the current HTML spec (Firefox 155+) fetch a failed
    // module again, so the original URL is tried first.
    const loaders = [
      () => importModuleUrl(moduleUrl, moduleUrl),
      () => importFromFreshUrls(moduleUrl, dependencyUrls),
    ];

    for (const load of loaders) {
      try {
        return await load();
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) {
          throw retryError;
        }
        lastError = retryError;
      }
    }
  }

  throw lastError;
};

const importModuleUrl = async (moduleUrl: string, url: string) => {
  await import(/* @vite-ignore */ url);
  recoveredModuleUrls.set(moduleUrl, url);
  return url;
};

/**
 * A chunk also fails when one of the chunks it imports failed before, and a
 * new URL for the chunk alone imports that failed chunk again. So the
 * dependencies that fail get new URLs too, and an import map points the
 * imports of the newly loaded chunks to them. Dependencies that loaded keep
 * their URL, so no module is evaluated twice.
 *
 * Runs one at a time, so a dependency shared by two failed chunks gets only
 * one new URL.
 */
const importFromFreshUrls = (moduleUrl: string, dependencyUrls: string[]) => {
  const freshUrlImport = freshUrlImports.then(async () => {
    const replacedDependencyUrls = await replaceFailedDependencyUrls(
      dependencyUrls,
    );
    const freshModuleUrl = getCacheBustedUrl(moduleUrl);

    addImportMap(
      [freshModuleUrl, ...replacedDependencyUrls],
      getRecoveredDependencyUrls(dependencyUrls),
    );

    return importModuleUrl(moduleUrl, freshModuleUrl);
  });

  freshUrlImports = freshUrlImport.catch(() => undefined);
  return freshUrlImport;
};

/**
 * A dependency only gets a new URL when the browser holds on to its failure
 * while the file itself loads. A new URL for a file that cannot be fetched
 * does not help, and in browsers that fetch failed modules again it could
 * evaluate the module a second time once both URLs load.
 */
const replaceFailedDependencyUrls = async (dependencyUrls: string[]) => {
  const replacedUrls = await Promise.all(
    dependencyUrls.map(async dependencyUrl => {
      const currentUrl =
        recoveredModuleUrls.get(dependencyUrl) ?? dependencyUrl;

      if (!(await isFailureKeptByBrowser(currentUrl))) {
        return undefined;
      }

      const freshUrl = getCacheBustedUrl(dependencyUrl);
      recoveredModuleUrls.set(dependencyUrl, freshUrl);
      return freshUrl;
    }),
  );

  return replacedUrls.filter(url => url !== undefined);
};

const isFailureKeptByBrowser = async (url: string) => {
  try {
    await import(/* @vite-ignore */ url);
    return false;
  } catch (error) {
    return isChunkLoadError(error) && (await canFetch(url));
  }
};

/**
 * Only the headers are requested, since the file is downloaded again from its
 * new URL. Bypasses the HTTP cache, which may still hold an error response the
 * CDN once allowed browsers to keep.
 */
const canFetch = (url: string) =>
  fetch(url, {method: 'HEAD', cache: 'reload'}).then(
    response => response.ok,
    () => false,
  );

const getRecoveredDependencyUrls = (dependencyUrls: string[]) =>
  Object.fromEntries(
    dependencyUrls.flatMap(dependencyUrl => {
      const recoveredUrl = recoveredModuleUrls.get(dependencyUrl);
      return recoveredUrl && recoveredUrl !== dependencyUrl
        ? [[dependencyUrl, recoveredUrl]]
        : [];
    }),
  );

/**
 * Import maps added after the page started loading modules only apply to
 * imports no module has resolved yet, which holds for imports from the new
 * URLs these scopes are keyed by. Browsers without support ignore the map.
 */
const addImportMap = (scopeUrls: string[], imports: Record<string, string>) => {
  if (Object.keys(imports).length === 0) {
    return;
  }

  const importMap = document.createElement('script');
  importMap.type = 'importmap';
  importMap.textContent = JSON.stringify({
    scopes: Object.fromEntries(scopeUrls.map(scopeUrl => [scopeUrl, imports])),
  });
  document.head.append(importMap);
};

const getCacheBustedUrl = (moduleUrl: string) => {
  const attempt = (cacheBustedAttempts.get(moduleUrl) ?? 0) + 1;
  cacheBustedAttempts.set(moduleUrl, attempt);

  const url = new URL(moduleUrl);
  url.searchParams.set('retry', String(attempt));
  return url.href;
};

const waitBeforeRetry = async (attempt: number) => {
  await new Promise(resolve => {
    setTimeout(resolve, RETRY_DELAY_MS * attempt);
  });
  await waitUntilPageCanLoadChunks();
};

const getLeavingPageTimeLeft = () =>
  leavingPageSince === undefined
    ? 0
    : leavingPageSince + ABANDONED_NAVIGATION_TIMEOUT_MS - Date.now();

const canLoadChunks = () =>
  document.visibilityState === 'visible' &&
  navigator.onLine &&
  getLeavingPageTimeLeft() <= 0;

const waitUntilPageCanLoadChunks = () =>
  new Promise<void>(resolve => {
    let abandonedNavigationTimer: number | undefined;

    const resolveOnceChunksCanLoad = () => {
      window.clearTimeout(abandonedNavigationTimer);

      if (!canLoadChunks()) {
        const leavingPageTimeLeft = getLeavingPageTimeLeft();
        if (leavingPageTimeLeft > 0) {
          abandonedNavigationTimer = window.setTimeout(
            resolveOnceChunksCanLoad,
            leavingPageTimeLeft,
          );
        }
        return;
      }

      document.removeEventListener(
        'visibilitychange',
        resolveOnceChunksCanLoad,
      );
      window.removeEventListener('online', resolveOnceChunksCanLoad);
      window.removeEventListener('pageshow', resolveOnceChunksCanLoad);
      resolve();
    };

    document.addEventListener('visibilitychange', resolveOnceChunksCanLoad);
    window.addEventListener('online', resolveOnceChunksCanLoad);
    window.addEventListener('pageshow', resolveOnceChunksCanLoad);
    resolveOnceChunksCanLoad();
  });
